import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, RotateCcw, Wrench, AlertTriangle, Power, Trash2, History, Lock, Database, Rocket, Wallet, KeyRound } from "lucide-react";
import { Card, Spinner, PageHeader, Badge, Button, Modal, Input, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import FreshStartWizard from "../components/FreshStartWizard";
import BackupCenter from "../components/BackupCenter";
import api from "../lib/api";

const RESET_SCOPES = [
  { id: "inventory", label: "Reset Inventory" },
  { id: "purchasing", label: "Reset Purchasing" },
  { id: "production", label: "Reset Production" },
  { id: "finance", label: "Reset Finance" },
  { id: "accounting", label: "Reset Accounting" },
  { id: "payroll", label: "Reset Payroll" },
  { id: "attendance", label: "Reset HR Attendance" },
  { id: "full", label: "Full System Reset" },
];

export default function DataIntegrity() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isOwner = user?.role === "OWNER" || isSuperAdmin;
  const canReset = hasPermission("system_reset");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetScope, setResetScope] = useState(null);
  const [showFreshStart, setShowFreshStart] = useState(false);
  const [showFactory, setShowFactory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/integrity/dashboard"); setD(data); }
    catch { toast.error("Gagal memuat"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const toggleMode = async () => {
    const next = !d.implementation_mode;
    if (!window.confirm(next ? "Aktifkan Implementation Mode? (Hard Delete & Reset diizinkan)" : "Beralih ke GO-LIVE? Hard Delete & Reset akan DINONAKTIFKAN permanen sampai diaktifkan lagi.")) return;
    try { await api.put("/integrity/mode", { implementation_mode: next }); toast.success(next ? "Implementation Mode AKTIF" : "GO-LIVE Mode aktif"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  if (loading || !d) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;

  const implMode = d.implementation_mode;
  const cards = [
    { label: "Reverse Transaksi", value: d.total_reverses, icon: RotateCcw },
    { label: "Force Correction", value: d.total_corrections, icon: Wrench },
    { label: "Koreksi Tertunda", value: d.pending_corrections, icon: History },
    { label: "Penyesuaian Inventory", value: d.inventory_adjustments, icon: Database },
    { label: "Penyesuaian Akuntansi", value: d.accounting_adjustments, icon: ShieldCheck },
  ];

  return (
    <div data-testid="data-integrity-page">
      <PageHeader title="Data Integrity" subtitle="Reverse, Force Correction & kontrol mode implementasi" icon={ShieldCheck} />

      {/* System status banner */}
      <div className={`mb-5 flex flex-col gap-3 rounded-2xl border-2 p-5 sm:flex-row sm:items-center sm:justify-between ${implMode ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="integrity-status-banner">
        <div className="flex items-center gap-3">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${implMode ? "bg-amber-400 text-amber-900" : "bg-emerald-500 text-white"}`}>
            {implMode ? <Wrench size={24} /> : <Lock size={24} />}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-mute">Status Sistem</p>
            <p className={`font-heading text-2xl font-bold ${implMode ? "text-amber-700" : "text-emerald-700"}`} data-testid="integrity-mode-label">
              {implMode ? "IMPLEMENTATION MODE" : "GO-LIVE MODE"}
            </p>
            <p className="text-xs text-ink-soft">{implMode ? "Hard Delete & System Reset diizinkan (untuk membersihkan data uji)" : "Data historis terlindungi — hanya Reverse & Force Correction"}</p>
          </div>
        </div>
        {isSuperAdmin && (
          <Button variant={implMode ? "outline" : "primary"} onClick={toggleMode} data-testid="integrity-mode-toggle">
            <Power size={15} /> {implMode ? "Beralih ke Go-Live" : "Aktifkan Implementation Mode"}
          </Button>
        )}
      </div>

      {/* Production Backup Center (Owner/SA — available in BOTH modes) */}
      {isOwner && <BackupCenter onRestored={load} />}

      {/* Fresh Start Wizard launcher (Owner-only, Implementation Mode only) */}
      {implMode && isOwner && (
        <Card className="mb-5 border-2 border-primary/30" data-testid="freshstart-launcher">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Rocket size={24} /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-ink">Fresh Start Wizard</h3>
                <p className="text-sm text-ink-soft">Pusat Persiapan Go-Live: backup pengaman → bersihkan data uji coba → saldo awal → aktifkan Production Mode. Master Data selalu dipertahankan.</p>
              </div>
            </div>
            <Button onClick={() => setShowFreshStart(true)} className="shrink-0" data-testid="freshstart-open-btn">
              <Rocket size={15} /> Mulai Fresh Start
            </Button>
          </div>
        </Card>
      )}

      {/* Factory Reset launcher (Owner-only, Implementation Mode only) — TRUE clean slate */}
      {implMode && isOwner && (
        <Card className="mb-5 border-2 border-red-300 bg-red-50/40" data-testid="factory-reset-launcher">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700"><Trash2 size={24} /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-red-700">Factory Reset</h3>
                <p className="text-sm text-ink-soft">Reset SELURUH data bisnis ke kondisi tenant baru: cabang, pengguna, item, resep/BOM, stok, pembelian, penjualan, persiapan, waste, opname & data uji. <b>Logika ERP, engine, AI & konfigurasi TIDAK dihapus.</b> Backup pengaman dibuat & diverifikasi otomatis sebelum eksekusi.</p>
              </div>
            </div>
            <Button variant="danger" onClick={() => setShowFactory(true)} className="shrink-0" data-testid="factory-reset-open-btn">
              <Trash2 size={15} /> Factory Reset
            </Button>
          </div>
        </Card>
      )}

      {/* Beginning Balance launcher (Owner) */}
      {isOwner && (
        <Card className="mb-5" data-testid="bb-launcher">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700"><Wallet size={24} /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-ink">Beginning Balance</h3>
                <p className="text-sm text-ink-soft">Saldo awal keuangan per lokasi (Kas, Bank, Merchant Wallet, Piutang & Hutang). Jurnal saldo awal dibuat otomatis — persediaan diisi lewat First Opening Stock Take.</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/beginning-balance")} className="shrink-0" data-testid="bb-launcher-btn">
              <Wallet size={15} /> Buka Beginning Balance
            </Button>
          </div>
        </Card>
      )}

      {/* Production Activation Record (permanent) */}
      {d.go_live && (
        <Card className="mb-5 border-2 border-emerald-200" data-testid="golive-record-card">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700"><Rocket size={14} /> Catatan Aktivasi Produksi (Permanen)</p>
          <div className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex justify-between"><span className="text-ink-soft">Tanggal Go-Live</span><b data-testid="golive-record-date">{d.go_live.go_live_date}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Operasional Pertama</span><b>{d.go_live.first_operational_date}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Dieksekusi oleh</span><b>{d.go_live.executed_by}</b></div>
            <div className="flex justify-between"><span className="text-ink-soft">Referensi Backup</span><b className="font-mono text-xs">{d.go_live.backup_reference}</b></div>
          </div>
        </Card>
      )}

      {/* Dashboard cards */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="integrity-cards">
        {cards.map((c) => (
          <Card key={c.label} data-testid={`integrity-card-${c.label}`}>
            <c.icon size={18} className="mb-2 text-primary" />
            <p className="font-heading text-2xl font-bold text-ink">{c.value}</p>
            <p className="text-xs text-ink-mute">{c.label}</p>
          </Card>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Reset center */}
        {canReset && (
          <Card data-testid="integrity-reset-center">
            <h3 className="mb-1 font-heading font-bold text-ink">System Reset Center</h3>
            <p className="mb-3 text-xs text-ink-mute">Hanya tersedia saat Implementation Mode. Membersihkan data uji per modul (dengan alasan & audit).</p>
            {!implMode ? (
              <div className="flex items-center gap-2 rounded-xl bg-stone-100 px-4 py-3 text-sm text-ink-mute"><Lock size={15} /> Dinonaktifkan pada mode Go-Live.</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {RESET_SCOPES.map((s) => (
                  <button key={s.id} onClick={() => setResetScope(s)} data-testid={`integrity-reset-${s.id}`}
                    className={`rounded-xl border px-3 py-2.5 text-left text-sm font-semibold transition-colors ${s.id === "full" ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100" : "border-line text-ink-soft hover:border-primary hover:bg-stone-50"}`}>
                    {s.id === "full" && <AlertTriangle size={13} className="mb-0.5 inline" />} {s.label}
                  </button>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Recent activity */}
        <Card data-testid="integrity-audit">
          <h3 className="mb-2 flex items-center gap-2 font-heading font-bold text-ink"><History size={16} /> Aktivitas Terbaru</h3>
          {(d.recent_activity || []).length === 0 ? <p className="py-4 text-sm text-ink-mute">Belum ada aktivitas integritas.</p> : (
            <div className="space-y-1.5 max-h-80 overflow-y-auto">
              {d.recent_activity.map((a) => (
                <div key={a.id} className="flex items-start justify-between border-b border-line py-1.5 text-sm last:border-0">
                  <div>
                    <span className="font-semibold text-ink">{ACTION_LABEL[a.action] || a.action}</span>
                    <span className="text-ink-mute"> · {a.module}</span>
                    {a.reason && <p className="text-xs text-ink-mute">"{a.reason}"</p>}
                  </div>
                  <span className="shrink-0 text-xs text-ink-mute">{(a.timestamp || "").slice(0, 16).replace("T", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Owner PIN (authorization for sensitive stock takes) */}
      {isOwner && <OwnerPinCard toast={toast} />}

      <ResetModal scope={resetScope} onClose={() => setResetScope(null)} onDone={() => { setResetScope(null); load(); }} toast={toast} />
      <FreshStartWizard open={showFreshStart} onClose={() => setShowFreshStart(false)} onDone={load} />
      <FactoryResetModal open={showFactory} onClose={() => setShowFactory(false)} onDone={load} toast={toast} />
    </div>
  );
}

const ACTION_LABEL = {
  reverse: "Reverse", force_correct: "Force Correction", hard_delete: "Hard Delete",
  reset: "System Reset", set_mode: "Ubah Mode",
  fresh_start_backup: "Backup Go-Live", fresh_start_cleanup_started: "Fresh Start Dimulai",
  fresh_start_cleanup_completed: "Fresh Start Selesai", fresh_start_opening_balance: "Saldo Awal",
  beginning_balance_saved: "Beginning Balance Disimpan",
  beginning_balance_completed: "Beginning Balance Selesai",
  owner_pin_changed: "Owner PIN Diganti", owner_pin_change_failed: "Ganti PIN Gagal",
  production_mode_enabled: "Production Mode Aktif",
  backup_created: "Backup Dibuat", backup_verified: "Backup Terverifikasi",
  backup_failed: "Backup Gagal", backup_expired: "Backup Kedaluwarsa (retensi)",
  backup_settings_updated: "Pengaturan Backup",
  restore_started: "Restore Dimulai", restore_completed: "Restore Selesai",
  restore_failed: "Restore Gagal",
};

function OwnerPinCard({ toast }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/integrity/owner-pin").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const change = async () => {
    if (form.next !== form.confirm) return toast.error("Konfirmasi PIN baru tidak cocok");
    setBusy(true);
    try {
      await api.put("/integrity/owner-pin", { current_pin: form.current, new_pin: form.next });
      toast.success("Owner PIN berhasil diganti");
      setForm({ current: "", next: "", confirm: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengganti PIN"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="mt-5" data-testid="owner-pin-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading font-bold text-ink"><KeyRound size={16} /> Owner PIN</h3>
          <p className="text-xs text-ink-mute">Otorisasi untuk First Opening & Correction Reset Stock Take. PIN default sistem: 8899 — segera ganti.</p>
        </div>
        {status && (status.is_default
          ? <Badge tone="warn" data-testid="owner-pin-default-badge">PIN DEFAULT</Badge>
          : <Badge tone="success" data-testid="owner-pin-custom-badge">PIN KUSTOM</Badge>)}
      </div>
      <div className="grid gap-3 sm:grid-cols-4">
        <Input label="PIN saat ini" type="password" inputMode="numeric" value={form.current}
          onChange={(e) => setForm((s) => ({ ...s, current: e.target.value }))} data-testid="owner-pin-current" />
        <Input label="PIN baru (4-8 digit)" type="password" inputMode="numeric" value={form.next}
          onChange={(e) => setForm((s) => ({ ...s, next: e.target.value }))} data-testid="owner-pin-new" />
        <Input label="Ulangi PIN baru" type="password" inputMode="numeric" value={form.confirm}
          onChange={(e) => setForm((s) => ({ ...s, confirm: e.target.value }))} data-testid="owner-pin-confirm" />
        <div className="flex items-end">
          <Button onClick={change} loading={busy} disabled={!form.current || !form.next || !form.confirm} className="w-full" data-testid="owner-pin-submit">
            Ganti PIN
          </Button>
        </div>
      </div>
    </Card>
  );
}

function FactoryResetModal({ open, onClose, onDone, toast }) {
  const [prev, setPrev] = useState(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) { setReason(""); setConfirm(""); setPrev(null); return; }
    api.get("/integrity/factory-reset/preview").then(({ data }) => setPrev(data)).catch(() => toast.error("Gagal memuat ringkasan"));
  }, [open, toast]);
  if (!open) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrity/factory-reset", { confirm_text: confirm, reason });
      toast.success(`Factory Reset selesai. ${data.wiped_documents} data dihapus · ${data.verify.branches} cabang & ${data.verify.users} pengguna dibuat.`);
      onDone();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="⚠️ Factory Reset" size="md"
      footer={<><Button variant="outline" onClick={onClose} data-testid="factory-reset-cancel">Batal</Button>
        <Button variant="danger" onClick={submit} loading={busy}
          disabled={confirm.trim().toUpperCase() !== "FACTORY RESET" || !reason.trim()}
          data-testid="factory-reset-submit"><Trash2 size={15} /> Confirm Factory Reset</Button></>}>
      <div className="space-y-3" data-testid="factory-reset-modal">
        <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-xs text-red-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Ini akan <b>menghapus permanen seluruh data operasional</b>: cabang, pengguna, item, resep, stok, pembelian, penjualan, persiapan, waste & data uji.
          <b> Logika & konfigurasi ERP TIDAK dihapus.</b> Tindakan ini tidak dapat dibatalkan (backup pengaman dibuat otomatis).
        </div>
        {prev && (
          <>
            <div className="rounded-xl border border-line p-3 text-sm">
              <p className="mb-1.5 font-semibold text-ink">Akan dihapus: {prev.total_documents?.toLocaleString?.() || prev.total_documents} data</p>
              <div className="grid max-h-40 grid-cols-2 gap-x-4 gap-y-0.5 overflow-y-auto text-xs text-ink-soft" data-testid="factory-reset-categories">
                {(prev.categories || []).map((c) => (
                  <div key={c.label} className="flex justify-between"><span>{c.label}</span><b className="text-ink">{c.count}</b></div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
              <p className="mb-1 font-semibold">Setelah reset (baseline bersih):</p>
              <p><b>Cabang:</b> {(prev.branches_after || []).join(", ")}</p>
              <p className="mt-0.5"><b>Pengguna:</b> {(prev.users_after || []).join(", ")}</p>
              <p className="mt-0.5">Central/HQ tetap dipertahankan. Tidak ada item/resep/stok demo yang dibuat.</p>
            </div>
          </>
        )}
        <Input label="Alasan (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="factory-reset-reason" />
        <Input label='Ketik "FACTORY RESET" untuk melanjutkan' value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="factory-reset-confirm" />
      </div>
    </Modal>
  );
}

function ResetModal({ scope, onClose, onDone, toast }) {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); setConfirm(""); }, [scope]);
  if (!scope) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrity/reset", { scope: scope.id, confirm, reason });
      toast.success(`${scope.label} selesai. Buku besar disinkron ulang.`);
      console.log("reset deleted:", data.deleted);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={scope.label} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Batal</Button><Button variant="danger" onClick={submit} loading={busy} disabled={confirm !== "RESET" || !reason.trim()} data-testid="integrity-reset-submit"><Trash2 size={15} /> Reset</Button></>}>
      <div className="space-y-3" data-testid="integrity-reset-modal">
        <div className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-xs text-red-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> Tindakan ini menghapus data {scope.label.replace("Reset ", "")} secara permanen lalu menyinkron ulang buku besar. Tidak dapat dibatalkan.
        </div>
        <Input label="Alasan (wajib)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="integrity-reset-reason" />
        <Input label='Ketik "RESET" untuk konfirmasi' value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="integrity-reset-confirm" />
      </div>
    </Modal>
  );
}
